import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTeam } from '@/api';
import type { TeamData } from '@/api';

export default function TeamPage() {
  const navigate = useNavigate();
  const [teamData, setTeamData] = useState<TeamData | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    getTeam()
      .then((res) => {
        if (mounted) setTeamData(res);
      })
      .catch((err) => {
        if (mounted) setError(err instanceof Error ? err.message : '加载失败');
      });
    return () => {
      mounted = false;
    };
  }, []);

  //加载中/失败
  if (!teamData) {
    return (
      <div className="mobile-container pb-10">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-sm border-b border-background-100">
          <div className="flex items-center justify-between px-4 py-3.5">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-xl text-foreground-700"></i>
              </button>
              <h1 className="text-base font-semibold text-foreground-900">我的团队</h1>
            </div>
          </div>
        </header>
        <div className="flex flex-col items-center justify-center py-24">
          {error ? (
            <>
              <i className="ri-wifi-off-line text-foreground-300 text-4xl mb-3" />
              <p className="text-foreground-400 text-sm">{error}</p>
            </>
          ) : (
            <>
              <i className="ri-loader-4-line text-primary-400 text-3xl animate-spin mb-3" />
              <p className="text-foreground-400 text-sm">加载中...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  const hasMembers = teamData.members.length > 0;
  const progress = Math.min((teamData.leaderCondition.current / teamData.leaderCondition.requirement) * 100, 100);

  const stats = [
    { label: '团队总人数', value: teamData.teamTotal, icon: 'ri-team-line' },
    { label: '直接下级', value: teamData.directMembers, icon: 'ri-user-add-line' },
    { label: '会员数', value: teamData.memberCount, icon: 'ri-vip-crown-line' },
    { label: '团长数', value: teamData.leaderCount, icon: 'ri-star-line' },
  ];

  return (
    <div className="mobile-container pb-10">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-sm border-b border-background-100">
        <div className="flex items-center justify-between px-4 py-3.5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl text-foreground-700"></i>
            </button>
            <h1 className="text-base font-semibold text-foreground-900">我的团队</h1>
          </div>
          <button
            onClick={() => navigate('/commission-details')}
            className="flex items-center gap-1 text-primary-500 text-sm font-medium hover:text-primary-600 transition-colors cursor-pointer"
          >
            <span>奖金明细</span>
            <i className="ri-arrow-right-s-line text-sm"></i>
          </button>
        </div>
      </header>

      <div className="px-4 space-y-4 pt-4">
        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-2">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="bg-white rounded-2xl border border-background-100 px-2 py-3 flex flex-col items-center text-center hover:border-background-200 transition-colors"
            >
              <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-50 mb-1.5">
                <i className={`${stat.icon} text-primary-400 text-sm`}></i>
              </div>
              <span className="text-foreground-900 text-lg font-bold">{stat.value}</span>
              <span className="text-foreground-400 text-[10px] mt-0.5">{stat.label}</span>
            </div>
          ))}
        </div>

        {/* Leader condition card */}
        <div className="bg-amber-50 border border-amber-200/70 rounded-2xl px-4 py-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-star-fill text-amber-500 text-sm"></i>
            </div>
            <span className="text-amber-700 text-xs font-semibold">团长资格解锁条件</span>
          </div>
          <p className="text-amber-700/90 text-xs leading-relaxed">
            直接推荐 {teamData.leaderCondition.requirement} 人（已达 {teamData.leaderCondition.current}/{teamData.leaderCondition.requirement}）
            且团队纵向达{teamData.leaderCondition.depth}层深度，即可升级为团长
          </p>
          {/* Progress bar */}
          <div className="mt-3">
            <div className="flex items-center justify-between text-[10px] mb-1">
              <span className="text-amber-600">{teamData.leaderCondition.current}/{teamData.leaderCondition.requirement} 推荐人</span>
              <span className="text-amber-600">{Math.round(progress)}%</span>
            </div>
            <div className="h-1.5 bg-amber-200/60 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-400 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Section title */}
        <div className="flex items-center gap-2 pt-1">
          <div className="w-1 h-4 rounded-full bg-primary-500"></div>
          <span className="text-sm font-semibold text-foreground-800">直接下级成员</span>
        </div>

        {/* Members or Empty state */}
        {hasMembers ? (
          <div className="space-y-3">
            {teamData.members.map((member) => (
              <div
                key={member.id}
                className="bg-white rounded-2xl px-4 py-4 border border-background-100 hover:border-background-200 transition-colors flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-500 font-bold text-sm">{member.name.charAt(0)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-800 text-sm font-medium">{member.name}</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-primary-50 text-primary-500 text-[10px] font-medium">
                      {member.level}
                    </span>
                  </div>
                  <p className="text-foreground-400 text-xs mt-0.5">{member.phone}</p>
                </div>
                <div className="text-right">
                  <p className="text-foreground-400 text-xs">{member.joinDate}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <div className="relative mb-6">
              <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-team-line text-foreground-300 text-3xl"></i>
              </div>
              <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center">
                <i className="ri-add-line text-primary-400 text-sm"></i>
              </div>
            </div>
            <p className="text-foreground-700 text-sm font-semibold mb-1.5">暂无团队成员</p>
            <p className="text-foreground-400 text-xs text-center leading-relaxed mb-6">
              您还没有发展任何团队成员
              <br />
              邀请好友加入即可组建您的团队
            </p>
            <button
              onClick={() => navigate('/invite')}
              className="px-6 py-2.5 rounded-full border border-primary-300 text-primary-500 text-xs font-medium hover:bg-primary-50 transition-colors cursor-pointer"
            >
              邀请好友加入
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
